// import { useNotification } from '../../context/NotificationContext';
// const { showNotification } = useNotification();

// showNotification("Form submitted successfully!", "success");
// showNotification("Failed to fetch data. Please try again.", "error");
// showNotification("Your password is weak. Consider using a stronger one.", "warning");
// showNotification("A new version of the app is available.", "info");
// showNotification("This message will disappear in 7 seconds.", "info", 7000);

// showNotification("Redirecting to dashboard...", "info");
// setTimeout(() => window.location.href = "/dashboard", 3000);

// src/context/NotificationContext.js
import React, { createContext, useContext, useState } from 'react';
import { Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Create Notification Context
const NotificationContext = createContext();

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [errorNotification, setErrorNotification] = useState(null); // Only one error at a time

    // Default durations based on best practices
    const defaultDurations = {
        success: 3000, // Short and non-intrusive
        info: 4000, // Slightly longer for informational messages
        warning: 5000, // Gives users time to notice warnings
        error: 4000, // Stays open until dismissed (user must close)
    };

    // Function to show a notification
    const showNotification = (message, severity = "info", duration = null) => {
        const notificationDuration = duration ?? defaultDurations[severity];

        if (severity === "error") {
            // Replace previous error notification (not stackable)
            setErrorNotification({ message, severity, duration: notificationDuration });
        } else {
            // Allow stacking for other notification types
            const id = new Date().getTime();
            setNotifications((prev) => [...prev, { id, message, severity, duration: notificationDuration }]);
        }
    };

    // Function to remove a specific notification
    const removeNotification = (id) => {
        setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    };

    // Function to clear the error notification
    const clearErrorNotification = () => {
        setErrorNotification(null);
    };

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}

            {/* Render Non-Error Notifications (Stackable) */}
            {notifications.map(({ id, message, severity, duration }) => (
                <Snackbar
                    key={id}
                    open
                    autoHideDuration={duration}
                    onClose={() => removeNotification(id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity={severity}
                        sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
                        action={
                            <IconButton size="small" color="inherit" onClick={() => removeNotification(id)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        {message}
                    </Alert>
                </Snackbar>
            ))}

            {/* Render Error Notification (Not Stackable) */}
            {errorNotification && (
                <Snackbar
                    open
                    onClose={clearErrorNotification}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity="error"
                        sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
                        action={
                            <IconButton size="small" color="inherit" onClick={clearErrorNotification}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        {errorNotification.message}
                    </Alert>
                </Snackbar>
            )}
        </NotificationContext.Provider>
    );
};

// Hook to use the notification system
export const useNotification = () => useContext(NotificationContext);
